import React from 'react'
import { CSSObject } from '@emotion/react'

import { Box, DisplayPosition, Image, withCitrusTheme } from '@gousto-internal/citrus-react'

import heroBackgroundDesktop from '../../../../assets/HeroAssets/hero-desktop.jpg'
import heroBackgroundDesktopWebp from '../../../../assets/HeroAssets/hero-desktop.webp'
import heroBackgroundMobileTablet from '../../../../assets/HeroAssets/hero-kitchen.jpg'
import heroBackgroundMobileTabletWebp from '../../../../assets/HeroAssets/hero-kitchen.webp'

const BACKGROUND_ALT =
  'A kitchen counter with a Gousto box overflowing with fresh ingredients and a plated meal'

// Three source crops of the hero, one shown per breakpoint band (display toggled, so only the
// relevant one is painted). Desktop uses the wide landscape asset; mobile + tablet use the
// portrait kitchen asset but framed differently.
//
// Mobile (Figma frame 183:180) scales the kitchen image up and shifts it left to frame the box —
// more zoomed-in than object-fit cover gives on our container, so we position the raw <img> rather
// than fighting object-fit. `width`/`left` are tuned together so the Gousto box's right vertical
// edge sits at the right screen edge while the whole spaghetti plate stays in view (zooming out with
// box-right pinned pulls the plate in from the left); `top` keeps the top covered in our 812px
// mobile hero (the design frame is 917px, so its top % doesn't transfer).
const HeroBackgroundMobileImage = withCitrusTheme(Image, (theme) => [
  {
    position: 'absolute',
    width: '272%',
    height: 'auto',
    left: '-83%',
    top: '-5px',
    maxWidth: 'none',
    // Small phones (≤420px, e.g. iPhone SE 375×667): zoom out a touch (smaller box) AND shift the
    // image down (`top`) so the box + produce clear the eyebrow/headline instead of colliding with
    // it. The down-shift lifts the image off the top edge; HeroRoot's dark bg covers that strip
    // seamlessly. `left` follows the box-right-at-edge identity `left = 100 − 0.672·width`.
    '@media (max-width: 420px)': {
      width: '290%',
      left: '-95%',
      top: '60px',
    },
  } as CSSObject,
  // The 640–767 band is still mobile (tablet only starts at 768, matching the headline/layout), so
  // the mobile crop stays visible through index 1; the tablet crop takes over only at index 2.
  theme.responsiveStyle(['block', 'block', 'none', 'none'], ['display']) as CSSObject,
])
// Tablet (Figma frame 183:134 / 183:135, 820×1160) centres the kitchen image with object-fit cover
// (objectPosition 50%). A 1.14 scale sizes the box/plate to the design; translateY(64px) shifts the
// scene down to open the ~64px gap between the headline and the produce spilling out of the box (per
// the design). The produce sits high in the photo, so this shift lifts the image off the top edge —
// HeroRoot's dark background fills that strip seamlessly. overflow:hidden clips the bottom overshoot.
const HeroBackgroundTabletImage = withCitrusTheme(Image, (theme) => [
  {
    position: 'absolute',
    top: 0,
    left: 0,
    objectPosition: '50% 50%',
    transform: 'translateY(64px) scale(1.14)',
    transformOrigin: '50% 20%',
  } as CSSObject,
  // Tablet framing confined to 768–1023 (index 2) — the 640–767 band keeps the mobile crop above.
  theme.responsiveStyle(['none', 'none', 'block', 'none'], ['display']) as CSSObject,
])
const HeroBackgroundDesktopImage = withCitrusTheme(Image, (theme) => [
  { position: 'absolute', top: 0, left: 0, objectPosition: '50% 50%' } as CSSObject,
  theme.responsiveStyle(['none', 'none', 'none', 'block'], ['display']) as CSSObject,
])

// Desktop (index 3): horizontal, dark on the left behind the left-aligned content. Mobile (base +
// 640–767, indices 0–1) + tablet (768–1023, index 2): vertical, dark top + bottom behind the
// top-pinned header and bottom-pinned offer. The 640–767 band keeps the mobile gradient so it stays
// consistent with the mobile crop + headline in that band (tablet framing starts at 768).
const MOBILE_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(7,9,11,0) 32.21%, rgba(7,9,11,0) 75%, rgba(0,0,0,0.65) 100%)'
const TABLET_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 7.33%, rgba(7,9,11,0) 45.32%, rgba(7,9,11,0) 80.84%, rgba(0,0,0,0.65) 100%)'
const DESKTOP_GRADIENT = 'linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 61.8%)'
const GradientOverlay = withCitrusTheme(Box, (theme) =>
  theme.responsiveStyle(
    [MOBILE_GRADIENT, MOBILE_GRADIENT, TABLET_GRADIENT, DESKTOP_GRADIENT],
    ['background'],
  ),
)

// The hero background: three breakpoint crops (only the active one painted) plus the dark gradient
// overlay behind the content. Each crop is a <picture>: browsers that support WebP load the
// ~50%-smaller .webp source; the rest fall back to the .jpg on the <img>. The <img> keeps all the
// per-breakpoint positioning + display styling (mobile zoom, tablet transform, desktop cover), so
// the tuned framing is unchanged. display:contents keeps the wrapper from adding a layout box.
// Mobile + tablet share the kitchen asset; desktop uses the wide asset.
export function CWCHeroBackground() {
  return (
    <>
      <picture style={{ display: 'contents' }}>
        <source srcSet={heroBackgroundMobileTabletWebp} type="image/webp" />
        <HeroBackgroundMobileImage src={heroBackgroundMobileTablet} alt={BACKGROUND_ALT} />
      </picture>
      <picture style={{ display: 'contents' }}>
        <source srcSet={heroBackgroundMobileTabletWebp} type="image/webp" />
        <HeroBackgroundTabletImage
          src={heroBackgroundMobileTablet}
          alt={BACKGROUND_ALT}
          fit="cover"
          width="100%"
          height="100%"
        />
      </picture>
      <picture style={{ display: 'contents' }}>
        <source srcSet={heroBackgroundDesktopWebp} type="image/webp" />
        <HeroBackgroundDesktopImage
          src={heroBackgroundDesktop}
          alt={BACKGROUND_ALT}
          fit="cover"
          width="100%"
          height="100%"
        />
      </picture>
      <GradientOverlay
        position={DisplayPosition.Absolute}
        top="0"
        left="0"
        width="100%"
        height="100%"
      />
    </>
  )
}
